/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationJob } from './migration.models';

interface CheckRow {
  level: 'pass' | 'warn' | 'error' | 'unverified';
  labelKey: string;
}

/**
 * Validation for a saved job. Client-side checks run always and are honest
 * about their scope; the deep checks (connectivity, duplicates, sizes,
 * destination space) need the engine and are shown as "not verified" until
 * it answers.
 */
@Component({
  selector: 'app-mig-validation',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-validation.component.html'
})
export class MigrationValidationComponent implements OnInit {
  private readonly service = inject(MigrationService);
  readonly api = inject(MigrationApiService);

  readonly jobs = signal<MigrationJob[]>([]);
  readonly selectedId = signal('');
  readonly serverResult = signal<any>(null);
  readonly serverBusy = signal(false);

  readonly serverChecks: { labelKey: string }[] = [
    { labelKey: 'DMS.MIGRATION.VCHK_CONNECTIVITY' },
    { labelKey: 'DMS.MIGRATION.VCHK_DEST_ACCESS' },
    { labelKey: 'DMS.MIGRATION.VCHK_TYPES' },
    { labelKey: 'DMS.MIGRATION.VCHK_METADATA_MAP' },
    { labelKey: 'DMS.MIGRATION.VCHK_REQUIRED_PROPS' },
    { labelKey: 'DMS.MIGRATION.VCHK_USER_MAP' },
    { labelKey: 'DMS.MIGRATION.VCHK_PERM_MAP' },
    { labelKey: 'DMS.MIGRATION.VCHK_FILENAMES' },
    { labelKey: 'DMS.MIGRATION.VCHK_SIZE' },
    { labelKey: 'DMS.MIGRATION.VCHK_DUPLICATES' },
    { labelKey: 'DMS.MIGRATION.VCHK_CHECKSUM' },
    { labelKey: 'DMS.MIGRATION.VCHK_STORAGE' },
    { labelKey: 'DMS.MIGRATION.VCHK_VERSION_COMPAT' }
  ];

  ngOnInit(): void {
    this.service.jobs().subscribe((list) => this.jobs.set(list));
  }

  get job(): MigrationJob | undefined {
    return this.jobs().find((j) => j.id === this.selectedId());
  }

  pick(id: string): void {
    this.selectedId.set(id);
    this.serverResult.set(null);
  }

  clientChecks(): CheckRow[] {
    const j = this.job;
    if (!j) {
      return [];
    }
    const rows: CheckRow[] = [];
    const push = (ok: boolean, labelKey: string, warn = false) => rows.push({ level: ok ? 'pass' : warn ? 'warn' : 'error', labelKey });

    push(!!j.name?.trim(), 'DMS.MIGRATION.V_NAME');
    push(!!j.sourceConnector, 'DMS.MIGRATION.V_SOURCE');
    push(!!j.sourceConnectionId, 'DMS.MIGRATION.V_CONNECTION', true);
    push((j.selection?.paths?.length ?? 0) > 0, 'DMS.MIGRATION.V_SELECTION');
    push(j.duplicatePolicy !== 'replace', 'DMS.MIGRATION.V_DUPPOLICY', true);
    push((j.mapping?.metadata?.length ?? 0) > 0 || !!j.selection?.includeMetadata === false, 'DMS.MIGRATION.V_METADATA_MAP', true);
    return rows;
  }

  errorCount(): number {
    return this.clientChecks().filter((r) => r.level === 'error').length;
  }

  warnCount(): number {
    return this.clientChecks().filter((r) => r.level === 'warn').length;
  }

  passCount(): number {
    return this.clientChecks().filter((r) => r.level === 'pass').length;
  }

  runServerValidation(): void {
    const j = this.job;
    if (!j || this.api.available() !== true) {
      return;
    }
    this.serverBusy.set(true);
    this.api.validate(j).subscribe({
      next: (res) => {
        this.serverBusy.set(false);
        this.serverResult.set(res);
      },
      error: () => {
        this.serverBusy.set(false);
        this.serverResult.set({ error: true });
      }
    });
  }

  trackByRow(i: number): number {
    return i;
  }
}
